import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import styled from 'styled-components';
import { AnimatedStepBody } from '../animation/StepBodyAnimations';
import { useIsFramedCentredStep } from '../hooks/useIsFramedCentredStep';
import { getPostAccountCreationSteps } from '../store/flow/selectors';
import { devices } from '../styles/viewports.styles';
import { GreetingsPretitle } from '../views-components/GreetingsPretitle';
import { StepSubtitle } from '../views-components/StepSubtitle';
import { StepTitle } from '../views-components/StepTitle';
import { Step } from './Step';
import { LayoutTemplateBelowTitleContainer, LayoutTemplateContentContainer } from '../app/AppViewComponent.styles';
import { useLayoutTemplate } from '../hooks/useLayoutTemplate';
import { TrackWhen, useExperiment } from '../experiments/useExperiment';
import { ExperimentKeys } from '../experiments/ExperimentKeys';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { useIndustry } from '../step-industry/useIndustry';
import { useCurrentStep } from '../hooks/useCurrentStep';
import { getFlowConfig } from '../flow-initial/getFlowConfig';
import imageUrls from '../images/imageUrls';
import { OuterTitleContainer, InnerContainer, StyledImage } from '../styles/Stars.styles';
import { isInACQ140VariantACRM, isInACQ142VariantAMSS } from '../experiments/gswhV1/gswhUtils';
const BaseViewHeader = styled.header.withConfig({
  displayName: "BaseView__BaseViewHeader",
  componentId: "sc-3btd5l-0"
})(["margin-bottom:1.5rem;", " @media ", "{", "}@media ", "{", " ", "}"], props => !props.isPostAccountCreationStep && `text-align: center;`, devices.tablet, props => props.isACQ142Variant && `max-width: 816px; margin: auto;`, devices.desktop, props => props.isFramedCentredStep && props.stepKey === Step.HubletSelection && `margin-bottom: 0;`, props => props.isFramedCentredStep && props.stepKey === Step.JobRole && `margin-left: 20px;`);
const BaseViewHelperText = styled.div.withConfig({
  displayName: "BaseView__BaseViewHelperText",
  componentId: "sc-3btd5l-1"
})(["", " ", " ", " ", ""], props => props.isFramedCentredStep && ![Step.CrmExperienceLevel].includes(props.stepKey) && `text-align: left;`, props => !props.isPostAccountCreationStep && `text-align: center;`, props => props.stepKey === Step.HubletSelection && `text-align: left !important; `, props => props.isACQ142Variant && ![Step.HubletSelection, Step.Industry, Step.CompanySize, Step.HubOption].includes(props.stepKey) && `margin-bottom: 16px;`);
export const BaseView = ({
  title,
  helperText,
  footer,
  children,
  stepKey,
  navMarker,
  preTitleMessage = '',
  addExperimentClassName = false,
  disableAnimation = false,
  expandableSubTitleSection = null
}) => {
  const {
    currentStep
  } = useCurrentStep();
  const step = stepKey || currentStep;
  const {
    isFramedCentredStep
  } = useIsFramedCentredStep();
  const {
    layoutType
  } = useLayoutTemplate();
  const {
    isAssignedToVariant: isAssignedToVariantACQ0142
  } = useExperiment(ExperimentKeys.ACQ0142, TrackWhen.never);
  const {
    clickedToSeeAll,
    predictedIndustries
  } = useIndustry();
  const predictions = predictedIndustries.length > 0;
  const postAccountCreation = useSelector(getPostAccountCreationSteps);
  const isACQ140VariantACRM = useSelector(isInACQ140VariantACRM);
  const isACQ142VariantAMSS = useSelector(isInACQ142VariantAMSS);

  if (!step) {
    return null;
  }

  let shouldRestrictWidth = ![Step.CompanySize, Step.Industry, Step.ManagementLevel].includes(step);
  const isPostAccountCreationStep = postAccountCreation.includes(currentStep);
  const shouldUseWave = getFlowConfig().shouldUseWaveOnPostAccountCreation;

  if (isAssignedToVariantACQ0142()) {
    shouldRestrictWidth = ![Step.Industry, Step.ManagementLevel, Step.CompanySize, Step.AhaMoment, Step.UseCase].includes(step);
  }

  const shouldUpdateSubtitleWidth = !isACQ142VariantAMSS && [Step.UseCase, Step.AhaMoment].includes(step);

  const stepTitle = /*#__PURE__*/_jsx(StepTitle, {
    isFramedCentredStep: isFramedCentredStep,
    stepKey: step
  });

  const navMarkerName = navMarker || `${step}_VIEW`;
  const experimentSeleniumAttribute = addExperimentClassName ? {
    'data-selenium-experiment': 'EXPERIMENT'
  } : null;

  const getPretitle = () => {
    if (isACQ140VariantACRM && currentStep === Step.CompanyName) {
      return /*#__PURE__*/_jsx(OuterTitleContainer, {
        children: /*#__PURE__*/_jsxs(InnerContainer, {
          children: [/*#__PURE__*/_jsx(StyledImage, {
            src: imageUrls.stars
          }), /*#__PURE__*/_jsx(GreetingsPretitle, {
            hasPreTitle: isValidI18nKey(preTitleMessage)
          }), /*#__PURE__*/_jsx(StepSubtitle, {
            message: preTitleMessage
          })]
        })
      });
    }

    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(GreetingsPretitle, {
        hasPreTitle: isValidI18nKey(preTitleMessage)
      }), /*#__PURE__*/_jsx(StepSubtitle, {
        message: preTitleMessage
      })]
    });
  };

  return /*#__PURE__*/_jsxs(AnimatedStepBody, {
    dataStep: step,
    disableAnimation: disableAnimation,
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: navMarkerName
    }), /*#__PURE__*/_jsxs("div", Object.assign({
      "data-selenium": `${step}_VIEW`
    }, experimentSeleniumAttribute, {
      children: [/*#__PURE__*/_jsxs(BaseViewHeader // @ts-expect-error Expression produces a union type that is too complex to represent.
      , {
        isACQ142Variant: isAssignedToVariantACQ0142(),
        isFramedCentredStep: isFramedCentredStep,
        isPostAccountCreationStep: isPostAccountCreationStep && shouldUseWave,
        stepKey: step,
        children: [getPretitle(), title || stepTitle, /*#__PURE__*/_jsxs(LayoutTemplateBelowTitleContainer, {
          isPostAccountCreationStep: isPostAccountCreationStep && shouldUseWave,
          shouldRestrictWidth: shouldRestrictWidth,
          shouldUpdateSubtitleWidth: shouldUpdateSubtitleWidth,
          children: [expandableSubTitleSection, /*#__PURE__*/_jsx(BaseViewHelperText, {
            isACQ142Variant: isAssignedToVariantACQ0142(),
            isFramedCentredStep: isFramedCentredStep,
            isPostAccountCreationStep: isPostAccountCreationStep && shouldUseWave,
            stepKey: step,
            children: helperText
          })]
        })]
      }), /*#__PURE__*/_jsxs(LayoutTemplateContentContainer, {
        clickedToSeeAll: clickedToSeeAll || predictions,
        layoutType: layoutType,
        shouldRestrictWidth: shouldRestrictWidth,
        stepKey: step,
        children: [children, footer]
      })]
    }))]
  });
};